import React from 'react';
import { Loader2, CheckCircle, AlertCircle, Upload, FileText, Sparkles } from 'lucide-react';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';

interface ProcessingFeedbackProps {
  isProcessing: boolean;
  progress: number;
  message: string;
  error?: string;
  className?: string;
}

interface StageConfig {
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
}

const STAGE_CONFIGS: { [key: number]: StageConfig } = {
  0: { icon: Upload, color: 'text-blue-500', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  25: { icon: Upload, color: 'text-yellow-500', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' },
  50: { icon: FileText, color: 'text-purple-500', bgColor: 'bg-purple-50', borderColor: 'border-purple-200' },
  75: { icon: Sparkles, color: 'text-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-200' },
  100: { icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-50', borderColor: 'border-green-200' }
};

export function ProcessingFeedback({
  isProcessing,
  progress,
  message,
  error,
  className
}: ProcessingFeedbackProps) {
  if (!isProcessing && !error) return null;

  // Get current stage config based on progress
  const stageKey = Math.max(0, Math.min(100, Math.floor(progress / 25) * 25));
  const stageConfig = STAGE_CONFIGS[stageKey] || STAGE_CONFIGS[0];
  const CurrentIcon = error ? AlertCircle : stageConfig.icon;

  return (
    <div className={cn("w-full max-w-md mx-auto", className)}>
      <div className={cn(
        "p-6 rounded-lg border text-center space-y-4 transition-all duration-300",
        error
          ? "border-red-200 bg-red-50"
          : `${stageConfig.borderColor} ${stageConfig.bgColor}`
      )}>
        {/* Icon */}
        <div className="flex justify-center">
          <div className={cn(
            "w-12 h-12 rounded-full flex items-center justify-center",
            error ? "bg-red-100" : stageConfig.bgColor
          )}>
            {isProcessing ? (
              <Loader2 className={cn("h-6 w-6 animate-spin", error ? "text-red-500" : stageConfig.color)} />
            ) : (
              <CurrentIcon className={cn("h-6 w-6", error ? "text-red-500" : stageConfig.color)} />
            )}
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <h3 className="font-medium text-lg">
            {error ? 'Erro no Processamento' : message}
          </h3>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Progress */}
        {!error && isProcessing && (
          <div className="space-y-3">
            <div className="flex justify-center">
              <Badge variant="outline" className={stageConfig.borderColor}>
                {progress < 25 && 'Enviando'}
                {progress >= 25 && progress < 50 && 'Analisando'}
                {progress >= 50 && progress < 75 && 'Processando'}
                {progress >= 75 && 'Finalizando'}
              </Badge>
            </div>

            <div className="w-full">
              <Progress value={progress} className="w-full h-2" />
              <p className="text-xs text-muted-foreground mt-1">
                {progress.toFixed(0)}% completo
              </p>
            </div>
          </div>
        )}

        {/* Error recovery */}
        {error && (
          <div className="text-sm text-muted-foreground">
            Por favor, verifique o arquivo e tente novamente.
          </div>
        )}
      </div>
    </div>
  );
}